"use client";
import React, { useEffect, useState } from "react";
import subscriptionService from "@/services/subscription/subscription.service";
import { useQuery } from "@tanstack/react-query";
import {
  GetPlansResponse,
  Plan,
} from "@/services/subscription/subscription.types";
import { Nav, Footer } from "@/components/bnp-ui";

import { ServicePaypoint } from "@/components/pages/user/dashboard/service_paypoint/ServicePaypoint";
import { usePostHogAnalytics } from "@/contexts/PostHogAnalyticsContext";
import Link from "next/link";

const formatCurrency = (amount: number, currencyCode: string) => {
  try {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch (e) {
    return `${currencyCode} ${amount.toLocaleString()}`;
  }
};

const fallbackBasicPlan: Plan = {
  id: "basic-plan-fallback-id",
  name: "Standard Plan",
  type: "standard",
  service_id: "basic_plan",
  currency: "NGN",
  billing_period: "monthly",
  features: [
    "Submit Listings ",
    "Listings are least prioritized in search result and default pages",
    "Standard media support (HD photos)",
  ],
  description: "Perfect for getting started with essential exposure tools.",
  is_active: true,
  max_developments: 5,
  priority_access: false,
  exclusive_deals: false,
  monthly_discount: "0.00",
  yearly_discount: "0.00",
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  company_service: {
    service_id: "basic_plan",
    service_label: "Basic Plan",
    amount: 0,
    currency: "NGN",
  },
};

const PricingCard = ({
  plan,
  isBasic,
  onUpgrade,
}: {
  plan: Plan;
  isBasic: boolean;
  onUpgrade: (billingInterval: "monthly" | "yearly") => void;
}) => {
  const [billingInterval, setBillingInterval] = useState<"monthly" | "yearly">(
    "monthly",
  );
  const baseAmount = plan.company_service.amount || 0;
  const mDiscountPercent = parseFloat(plan.monthly_discount) || 0;
  const yDiscountPercent = parseFloat(plan.yearly_discount) || 0;

  let finalDisplayPrice = baseAmount;
  let originalPrice: number | null = null;
  let subtext = "";

  if (billingInterval === "monthly") {
    if (mDiscountPercent > 0) {
      originalPrice = baseAmount;
      finalDisplayPrice = baseAmount * (1 - mDiscountPercent / 100);
    } else {
      finalDisplayPrice = baseAmount;
    }
    subtext = "/mo";
  } else {
    // Annual Billing
    if (yDiscountPercent > 0) {
      originalPrice = baseAmount;
      // Effective discounted monthly price when paid annually
      finalDisplayPrice = baseAmount * (1 - yDiscountPercent / 100);
      subtext = `/mo (billed ₦${(finalDisplayPrice * 12).toLocaleString()}/yr)`;
    } else {
      finalDisplayPrice = baseAmount;
      subtext = `/mo (billed ₦${(baseAmount * 12).toLocaleString()}/yr)`;
    }
  }

  return (
    <div
      className={`w-full h-[558px] max-w-md bg-white rounded-3xl p-7 border transition-all duration-300 flex flex-col justify-between group ${
        isBasic
          ? "border-gray-200"
          : "border-gray-100 hover:border-[#133345]/20 hover:shadow-xl"
      }`}
    >
      <div>
        {/* Tier Name & Badge */}
        <div className="flex items-center justify-between">
          <span
            className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${
              isBasic
                ? "text-gray-600 bg-gray-100"
                : "text-[#133345] bg-[#133345]/10"
            }`}
          >
            {plan.type}
          </span>

          {/* Discount Tag */}
          {!isBasic && (
            <>
              {billingInterval === "monthly" && mDiscountPercent > 0 && (
                <span className="text-[10px] font-extrabold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-100">
                  {mDiscountPercent}% Off
                </span>
              )}
              {billingInterval === "yearly" && yDiscountPercent > 0 && (
                <span className="text-[10px] font-extrabold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-100">
                  {yDiscountPercent}% Off
                </span>
              )}
            </>
          )}
        </div>

        {/* Toggle Switch */}
        {!isBasic && (
          <div className="mt-4 flex items-center gap-2 bg-gray-50 py-1 px-2.5 rounded-xl border border-gray-100 w-fit">
            <span
              className={`text-[10px] font-bold ${billingInterval === "monthly" ? "text-[#133345]" : "text-gray-400"}`}
            >
              Monthly
            </span>
            <button
              className="relative inline-flex h-4 w-8 items-center rounded-full bg-gray-200 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setBillingInterval((prev) =>
                  prev === "monthly" ? "yearly" : "monthly",
                );
              }}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-[#133345] transition-transform ${
                  billingInterval === "yearly"
                    ? "translate-x-4"
                    : "translate-x-1"
                }`}
              />
            </button>
            <span
              className={`text-[10px] font-bold ${billingInterval === "yearly" ? "text-[#133345]" : "text-gray-400"}`}
            >
              Annually
            </span>
          </div>
        )}

        <h3
          className={`text-lg font-bold text-[#133345] transition-colors duration-300 ${isBasic ? "mt-4" : "mt-3"}`}
        >
          {plan.name}
        </h3>

        {/* Pricing Layout */}
        <div className="h-16 mt-3 flex flex-col justify-center">
          {baseAmount === 0 && isBasic ? (
            <div>
              <div className="text-3xl font-black text-[#133345]">₦0</div>
              <p className="text-xs font-semibold text-gray-400 mt-0.5">
                Free forever
              </p>
            </div>
          ) : (
            <div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-black text-[#133345]">
                  {formatCurrency(finalDisplayPrice, plan.currency)}
                </span>
                {originalPrice !== null &&
                  originalPrice > finalDisplayPrice && (
                    <span className="text-sm font-medium text-gray-400 line-through">
                      {formatCurrency(originalPrice, plan.currency)}
                    </span>
                  )}
              </div>
              <p className="text-xs font-semibold text-gray-400 mt-0.5">
                {subtext}
              </p>
            </div>
          )}
        </div>

        <div className="border-t border-gray-100 my-4" />

        {/* Features list */}
        <ul className="space-y-3">
          {plan.features.map((feature, idx) => (
            <li
              key={idx}
              className="flex items-start gap-2.5 text-xs text-gray-600 leading-tight"
            >
              <svg
                className={`w-4 h-4 shrink-0 mt-0.5 ${
                  isBasic ? "text-gray-400" : "text-emerald-500"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Action Button */}
      <div className="mt-auto">
        {isBasic ? (
          <Link
            href="/user/dashboard"
            className="w-full py-2.5 rounded-xl text-sm font-bold bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all text-center block"
          >
            Default Plan
          </Link>
        ) : (
          <button
            onClick={() => onUpgrade(billingInterval)}
            className="w-full py-2.5 rounded-xl text-sm font-bold bg-[#2d96a7] text-white hover:bg-[#257f8d] transition-all shadow-sm"
          >
            Choose This Plan
          </button>
        )}
      </div>
    </div>
  );
};

const RealtorUpgrade = () => {
  const ph = usePostHogAnalytics();
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [selectedBillingInterval, setSelectedBillingInterval] = useState<
    "monthly" | "yearly"
  >("monthly");
  const [showPaypoint, setShowPaypoint] = useState(false);

  // Funnel: upgrade plans viewed. Once per mount.
  useEffect(() => {
    ph.trackEvent("realtor_upgrade_viewed", {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, isLoading, isError, error } = useQuery<GetPlansResponse>({
    queryKey: ["subscription-plans"],
    queryFn: async () => {
      const response = await subscriptionService.GetPlans();
      return response?.data ?? response;
    },
    staleTime: 1000 * 60 * 10,
  });

  const getOrderedPlans = (): Plan[] => {
    const rawPlans = data?.data || [];
    const hasBasicPlan = rawPlans.some(
      (plan) => plan.type?.toLowerCase() === "standard",
    );

    let processedPlans = [...rawPlans];

    if (!hasBasicPlan) {
      processedPlans = [fallbackBasicPlan, ...processedPlans];
    } else {
      processedPlans.sort((a, b) => {
        const isABasic = a.type?.toLowerCase() === "standard";
        const isBBasic = b.type?.toLowerCase() === "standard";
        if (isABasic && !isBBasic) return -1;
        if (!isABasic && isBBasic) return 1;
        return 0;
      });
    }

    return processedPlans;
  };

  const plans = getOrderedPlans();

  const handleUpgradeClick = (
    plan: Plan,
    billingInterval: "monthly" | "yearly",
  ) => {
    setSelectedPlan(plan);
    setSelectedBillingInterval(billingInterval);
    setShowPaypoint(false);
    ph.trackAnalyticsEvent("plan_chosen", {
      plan_name: plan.name,
      billing_period: billingInterval,
    });
  };

  const handleConfirmUpgrade = () => {
    if (selectedPlan) {
      ph.trackEvent("realtor_upgrade_initiated", {
        plan_name: selectedPlan.name,
        billing_period: selectedBillingInterval,
      });
      ph.trackAnalyticsEvent("checkout_started", {
        plan_name: selectedPlan.name,
        billing_period: selectedBillingInterval,
      });
    }
    setShowPaypoint(true);
  };

  const handleCloseModal = () => {
    setSelectedPlan(null);
    setShowPaypoint(false);
  };

  return (
    <div>
      <Nav />

      <div className="w-fit h-fit mx-auto px-8 sm:px-12 md:px-20  flex justify-center mt-[40px]">
        <div
          className="w-[100%] h-full rounded-lg pt-12"
          style={{
            backgroundImage: "url('/assets/images/upgrade.png')",
            backgroundRepeat: "no-repeat",
            backgroundSize: "contain",
          }}
        >
          {/* Header Texts */}
          <div className="flex justify-center mb-12">
            <div>
              <h1 className="text-main-900 font-bold text-xl text-center">
                Upgrade your Realtor account
              </h1>
              <p className="text-sm text-main-900 text-center mt-4 max-w-lg mx-auto">
                Elevate your real estate business with premium tools and
                features designed to help you close more deals and provide
                exceptional service to your clients.
              </p>
            </div>
          </div>

          {/* Loader/Error Handling */}
          {isLoading && (
            <div className="flex items-center justify-center p-12 text-sm text-[#8A8FB9] font-medium">
              Fetching available plans...
            </div>
          )}

          {isError && (
            <div className="mx-4 p-4 bg-red-50 border border-red-100 text-red-600 rounded-xl text-sm font-medium">
              Failed to load subscription tiers:{" "}
              {error instanceof Error ? error.message : "Network error"}
            </div>
          )}

          {/* Pricing Grid */}
          {!isLoading && !isError && (
            <div className="w-full max-w-7xl mx-auto px-4 pb-12">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center items-stretch">
                {plans.map((plan) => {
                  const isBasic =
                    plan.type?.toLowerCase() === "standard" ||
                    plan.type?.toLowerCase() === "basic";

                  return (
                    <div
                      key={plan.id}
                      className="w-full min-w-[280px] max-w-[380px] flex justify-center"
                    >
                      <PricingCard
                        plan={plan}
                        isBasic={isBasic}
                        onUpgrade={(interval) =>
                          handleUpgradeClick(plan, interval)
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation & Paypoint Modal Overlay */}
      {selectedPlan && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={handleCloseModal}
        >
          {!showPaypoint ? (
            <div
              className="bg-white rounded-3xl shadow-2xl w-full max-w-md mx-auto p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-[#1E1B4B]">
                  Confirm Upgrade
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="bg-[#2d96a7]/5 rounded-xl p-5 mb-6 border border-[#2d96a7]/10">
                <p className="font-semibold text-[#2d96a7]">
                  {selectedPlan.name}
                </p>
                <p className="text-xs font-medium text-[#5C6189] mt-1 capitalize">
                  {selectedBillingInterval} billing
                </p>
              </div>

              <p className="text-sm text-[#5C6189] mb-8 leading-relaxed">
                You are about to upgrade to the{" "}
                <strong className="text-[#1E1B4B]">{selectedPlan.name}</strong>.
                Would you like to proceed with the payment?
              </p>

              <div className="flex gap-3">
                <button
                  onClick={handleCloseModal}
                  className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmUpgrade}
                  className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-[#2d96a7] text-white hover:bg-opacity-90 transition-all shadow-sm"
                >
                  Confirm & Pay
                </button>
              </div>
            </div>
          ) : (
            <ServicePaypoint
              service_units={selectedBillingInterval === "monthly" ? 1 : 12}
              service_id={selectedPlan.service_id}
              target_resource_id={selectedPlan.id}
              paymentPurpose="realtor_upgrade"
              returnUrl="/user/dashboard/profile"
              onClose={handleCloseModal}
            />
          )}
        </div>
      )}

      <Footer />
    </div>
  );
};

export default RealtorUpgrade;
